import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import PageWithNavbar from "@/components/PageWithNavbar";

interface Article {
  slug: string;
  title: string;
  description: string;
  date: string;
  category: string;
  readTime: string;
  content?: string;
}

export const News = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load article index
    fetch('/news/index.json')
      .then(res => res.json())
      .then(data => {
        setArticles(data.articles || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load articles:', err);
        setLoading(false);
      });
  }, []);

  const loadArticle = async (slug: string) => {
    try {
      const response = await fetch(`/news/${slug}.md`);
      const markdown = await response.text();
      
      const article = articles.find(a => a.slug === slug);
      if (article) {
        setSelectedArticle({ ...article, content: markdown });
      }
    } catch (err) {
      console.error('Failed to load article:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <section className="container py-24 sm:py-32">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Loading...</h2>
        </div>
      </section>
    );
  }

  if (selectedArticle) {
    return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold">
            Doyen{" "}
            <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
              News
            </span>
          </h1>
          <p className="text-xl text-muted-foreground mt-2 mb-6">
            Stay up to date with the latest features, improvements, and announcements from Doyen Trading.
          </p>
        </div>
        <section className="container">
          <div className="max-w mx-auto">
            <button
              onClick={() => setSelectedArticle(null)}
              className={`mb-8 ${buttonVariants({ variant: "outline" })}`}
            >
              ← Back to Articles
            </button>

            <article>
              <header className="mb-8">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  {selectedArticle.title}
                </h1>
                <p className="text-xl text-muted-foreground mb-4">
                  {selectedArticle.description}
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedArticle.date)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {selectedArticle.readTime}
                  </div>
                </div>
              </header>

              <div className="prose prose-lg dark:prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  components={{
                    h1: ({ children }) => <h1 className="text-3xl font-bold mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-2xl font-bold mb-3 mt-6">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-xl font-bold mb-2 mt-5">{children}</h3>,
                    p: ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground">
                        {children}
                      </blockquote>
                    ),
                    code: ({ children, className }) => {
                      const isInline = !className;
                      return isInline ? (
                        <code className="bg-muted px-1 py-0.5 rounded text-sm font-mono">{children}</code>
                      ) : (
                        <code className="block bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto">
                          {children}
                        </code>
                      );
                    },
                    pre: ({ children }) => <pre className="mb-4">{children}</pre>,
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        className="text-primary hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                  }}
                >
                  {selectedArticle.content}
                </ReactMarkdown>
              </div>
            </article>
          </div>
        </section>
      </div>
    </div>
    );
  }

  return (
  <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold">
            Doyen{" "}
            <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
              News
            </span>
          </h1>
          <p className="text-xl text-muted-foreground mt-2 mb-6">
            Stay up to date with the latest features, improvements, and announcements from Doyen Trading.
          </p>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No articles available yet.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((article) => (
              <Card
                key={article.slug}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => loadArticle(article.slug)}
              >
                <CardHeader>
                  <Badge className="w-fit mb-2">{article.category}</Badge>
                  <CardTitle className="text-xl">{article.title}</CardTitle>
                  <CardDescription className="text-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-3 w-3" />
                      {formatDate(article.date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      {article.readTime}
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{article.description}</p>
                  <div className="flex items-center text-primary font-semibold">
                    Read More <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
    </PageWithNavbar>
  );
};
